import type Parse from 'parse';

import { ParseMapper } from '../../parse.mapper';
import { Finding } from '@telespot/domain';
import { AnalysisTopology } from './parse-analysis.mapper';
import { PipelineStepTopology, PipelineTopology } from '../protocols';
import { ObjectTopology } from '../../parse.topology';
import { AssetTopology, SampleTopology } from '../acquisition';
import { AlgorithmTopology } from '../ai';

export enum FindingTopology {
  TABLE = 'Findings',
  CREATOR_ENTITY = 'creatorEntity',
  CREATOR_ID = 'creatorId',
  ANALYSIS = 'analysis',
  VERSION = 'version',
  TYPE = 'type',
  PIPELINE_STEP = 'pipelineStep',
  DATA = 'data',
  ORIGIN = 'origin',
  UUID = 'uuid',
  ORGANIZATION = 'organization',
}

export interface ParseFindingFromDomainOptions {
  parseAnalysis?: Parse.Object;
}

export class ParseFindingMapper extends ParseMapper {
  public fromDomain(finding: Finding, sampleId?: string, options?: ParseFindingFromDomainOptions): Parse.Object {
    const parseObject = this.parse.Object.fromJSON({
      className: FindingTopology.TABLE,
    });

    const parseAnalysis =
      options?.parseAnalysis ??
      (finding.analysisId
        ? this.subclasses.getSubclass(AnalysisTopology.TABLE).createWithoutData(finding.analysisId)
        : this.createNewAnalysis({
            assetId: finding.assetId,
            sampleId: sampleId,
            pipelineId: finding.pipelineId,
            creatorId: finding.creatorId,
            creatorEntity: finding.creatorEntity,
          }));

    parseObject.set({
      [FindingTopology.CREATOR_ENTITY]: finding.creatorEntity,
      [FindingTopology.CREATOR_ID]: finding.creatorId,
      [FindingTopology.ANALYSIS]: parseAnalysis,
      // [FindingTopology.VERSION]: finding.version,
      [FindingTopology.TYPE]: finding.type,
      [FindingTopology.PIPELINE_STEP]: this.subclasses
        .getSubclass(PipelineStepTopology.TABLE)
        .createWithoutData(finding.pipelineStepId),
      [FindingTopology.DATA]: finding.data,
      [FindingTopology.ORIGIN]: finding.originId
        ? this.subclasses.getSubclass(FindingTopology.TABLE).createWithoutData(finding.originId)
        : undefined,
      [FindingTopology.UUID]: finding.uuid,
    });

    return parseObject;
  }

  private createNewAnalysis({
    assetId,
    sampleId,
    pipelineId,
    creatorId,
    creatorEntity,
  }: {
    assetId?: string;
    sampleId: string;
    pipelineId: string;
    creatorId: string;
    creatorEntity: string;
  }): Parse.Object {
    const ParseSample = this.subclasses.getSubclass(SampleTopology.TABLE);
    const ParseAsset = this.subclasses.getSubclass(AssetTopology.TABLE);
    const ParsePipeline = this.subclasses.getSubclass(PipelineTopology.TABLE);
    const ParseAlgorithm = this.subclasses.getSubclass(AlgorithmTopology.TABLE);

    const creatorSubclass = creatorEntity === 'algorithm' ? ParseAlgorithm : this.parse.User;
    const creatorKey = creatorEntity === 'algorithm' ? AnalysisTopology.ALGORITHM : AnalysisTopology.CREATED_BY;

    const parseAnalysis = this.parse.Object.fromJSON({
      className: AnalysisTopology.TABLE,
    });

    parseAnalysis.set({
      [AnalysisTopology.SAMPLE]: ParseSample.createWithoutData(sampleId),
      [AnalysisTopology.ASSET]: assetId ? ParseAsset.createWithoutData(assetId) : undefined,
      [AnalysisTopology.PIPELINE]: ParsePipeline.createWithoutData(pipelineId),
      [creatorKey]: creatorSubclass.createWithoutData(creatorId),
    });

    return parseAnalysis;
  }

  public toDomain(object: Parse.Object): Finding {
    const analysis = object.get(FindingTopology.ANALYSIS);

    return new Finding({
      id: object.id,
      creatorEntity: object.get(FindingTopology.CREATOR_ENTITY),
      creatorId: object.get(FindingTopology.CREATOR_ID),
      data: object.get(FindingTopology.DATA),
      analysisId: analysis?.id,
      pipelineStepId: object.get(FindingTopology.PIPELINE_STEP)?.id,
      type: object.get(FindingTopology.TYPE),
      version: object.get(FindingTopology.VERSION),
      assetId: analysis?.get(AnalysisTopology.ASSET)?.id,
      pipelineId: analysis?.get(AnalysisTopology.PIPELINE)?.id,
      originId: object.get(FindingTopology.ORIGIN),
      uuid: object.get(FindingTopology.UUID),
      createdAt: object.get(ObjectTopology.CREATED_AT),
      updatedAt: object.get(ObjectTopology.UPDATED_AT),
    });
  }
}
